import { BaseSeratoFilter } from '@/pixi/filters/BaseSeratoFilter'
import { EffectState } from '@/pixi/filters/types'

interface MirrorUniforms {
  isAudioReactive: boolean,
  audioIntensity: number,
  numberOfMirrors: number,
  isVerticalMirror?: boolean,
  overlayIntensity: number,
  mirrorOffset: number,
  isFlipped?: boolean
}

interface MirrorFilterParams {
  isVerticalMirror?: boolean,
  numberOfMirrors: number,
  isFlipped?: boolean
}

export class MirrorFilter extends BaseSeratoFilter {

  get uniforms (): MirrorUniforms {
    return super.uniforms as MirrorUniforms
  }

  constructor (effectState: EffectState, params: MirrorFilterParams) {
    const shader = `
    #define reactivityIntensity 0.5
    
    // generic default params
    varying vec2 vTextureCoord;
    uniform sampler2D uSampler;

    // params to tune effects
    uniform float numberOfMirrors;
    uniform bool isVerticalMirror;
    uniform bool isFlipped;
    uniform float overlayIntensity;
    uniform float mirrorOffset;
    
    // audio uniforms
    uniform bool isAudioReactive;
    uniform float audioIntensity;

    // mirror segments must be offset in different directions depending on isVerticalMirror and isFlipped
    vec2 getMirrorOffset() {
      vec2 mirrorOffsetVector = vec2(0.0, 0.0);
      if (isVerticalMirror) {
        mirrorOffsetVector = vec2(0.0, 0.5 * mirrorOffset);
      } else {
        mirrorOffsetVector = vec2(0.5 * mirrorOffset, 0.0);
      }
      
      return isFlipped ? mirrorOffsetVector : -1.0 * mirrorOffsetVector;
    }

    vec2 getReactivityDelta() {
      vec2 reactivityDelta = vec2(0.0, 0.0);
      if (isAudioReactive) {
        float audioMultiplier = audioIntensity / 2.0;
        if (numberOfMirrors == 1.0) {
          reactivityDelta = isVerticalMirror ? vec2(0.0, audioMultiplier) : vec2(audioMultiplier, 0.0);
          if (isFlipped && mirrorOffset < 0.5 || !isFlipped && mirrorOffset > 0.5) {
            reactivityDelta = reactivityDelta * -1.0;
          }
        } else {
          reactivityDelta = vec2(audioMultiplier, audioMultiplier);
        }
      }
      return reactivityDelta * reactivityIntensity;
    }

    void main(void)
    {
      vec2 vtc = vTextureCoord; // shorter name

      // reactivity vector incorporates audio reactivity and mirror offset
      vec2 reactivityDelta = getReactivityDelta();
      
      // create segments mirrored vertically and horizontally
      vec2 mirrorOffset = getMirrorOffset();
      vec2 uv0 = vtc + mirrorOffset;
      vec2 uv1 = vec2(1.0 - vtc.x, vtc.y) + mirrorOffset;
      vec2 uv2 = vec2(vtc.x, 1.0 - vtc.y) + mirrorOffset;
      vec2 uv3 = vec2(1.0 - vtc.x, 1.0 - vtc.y) + mirrorOffset;

      // swap round the segments if flipped
      if (isFlipped) {
        vec2 uv0Temp = uv0;
        if (isVerticalMirror) {
          uv0 = uv2;
          uv2 = uv0Temp;
        } else {
          uv0 = uv1;
          uv1 = uv0Temp;
        }
      }
        
        // texture
      vec4 tex0 = texture2D(uSampler, uv0.xy - reactivityDelta);
      vec4 tex1 = texture2D(uSampler, uv1.xy - reactivityDelta);
      vec4 tex2 = texture2D(uSampler, uv2.xy - reactivityDelta);
      vec4 tex3 = texture2D(uSampler, uv3.xy - reactivityDelta);

      vec4 texColor;

      //assign a pixel to a segment depending on it's position
      if (numberOfMirrors == 2.0) {
        texColor = (vtc.x >= 0.5 && vtc.y >= 0.5) ? tex0 :
                  (vtc.x <= 0.5 && vtc.y >= 0.5) ? tex1 :
                  (vtc.x >= 0.5 && vtc.y <= 0.5) ? tex2 :
                                                  tex3;
      } else {
        if (isVerticalMirror) {
          texColor = (vtc.y >= 0.5) ? tex0 : tex2;
        } else {
          texColor = (vtc.x >= 0.5) ? tex0 : tex1;
        }
      }
        
      // add overlay only for quadMirror
      if (numberOfMirrors == 2.0) {
        vec4 overlay = max(tex0, tex1);
        overlay = max(overlay, tex2);
        overlay = max(overlay, tex3);
        texColor.rgb = (texColor.rgb + overlay.rgb * overlayIntensity) / (1.0 + overlayIntensity);
      }
      
      gl_FragColor = texColor.a == 0.0 ? vec4(0.0) : texColor;
    }`

    const uniforms: MirrorUniforms = {
      isAudioReactive: false,
      audioIntensity: 0,
      numberOfMirrors: params.numberOfMirrors,
      isVerticalMirror: params.isVerticalMirror,
      isFlipped: params.isFlipped,
      overlayIntensity: 0.5,
      mirrorOffset: 0.5
    }
    
    super(
      effectState,
      undefined,
      shader,
      uniforms
    )
  }

  updateUniforms () {
    this.uniforms.mirrorOffset = this.effectState.intensity
    this.uniforms.overlayIntensity = this.effectState.intensity
    this.uniforms.audioIntensity = this.getAudioIntensityValue()
    this.uniforms.isAudioReactive = this.effectState.audioReactivityEnabled
  }
}
